/**
 * Shell.View Helper Functions
 * Centralized UI element creation helpers for extensions
 */

import type { shell } from 'punica';
import { createStatusDot } from '../statusDot/index.js';

import capabilityCss from './capability.css';
import cardCss from './card.css';
import linkCss from './link.css';
import emptyStateCss from './emptyState.css';
import listItemCss from './listViewItem.css';
import searchAdornmentCss from './searchAdornment.css';
import secretAdornmentCss from './secretAdornment.css';

type CreateElementOptions = shell.View.Helpers.CreateElementOptions;
type ButtonOptions = shell.View.Helpers.ButtonOptions;
type IconButtonOptions = shell.View.Helpers.IconButtonOptions;
type TypographyOptions = shell.View.Helpers.TypographyOptions;
type ChipOptions = shell.View.Helpers.ChipOptions;
type BoxOptions = shell.View.Helpers.BoxOptions;
type RowOptions = shell.View.Helpers.RowOptions;
type ColOptions = shell.View.Helpers.ColOptions;
type InputOptions = shell.View.Helpers.InputOptions;
type SelectOptions = shell.View.Helpers.SelectOptions;
type SelectItemOptions = shell.View.Helpers.SelectItemOptions;
type CheckboxOptions = shell.View.Helpers.CheckboxOptions;
type IconOptions = shell.View.Helpers.IconOptions;
type TextareaOptions = shell.View.Helpers.TextareaOptions;
type DividerOptions = shell.View.Helpers.DividerOptions;
type ListViewOptions = shell.View.Helpers.ListViewOptions;
type ListViewItemOptions = shell.View.Helpers.ListViewItemOptions;
type PaperOptions = shell.View.Helpers.PaperOptions;
type TabOptions = shell.View.Helpers.TabOptions;
type TabItemOptions = shell.View.Helpers.TabItemOptions;
type TabPanelOptions = shell.View.Helpers.TabPanelOptions;
type BadgeOptions = shell.View.Helpers.BadgeOptions;
type MenuOptions = shell.View.Helpers.MenuOptions;
type MenuAnchorOptions = shell.View.Helpers.MenuAnchorOptions;
type MenuItemOptions = shell.View.Helpers.MenuItemOptions;
type SplitOptions = shell.View.Helpers.SplitOptions;
type ContextMenuOptions = shell.View.Helpers.ContextMenuOptions;
type LinkOptions = shell.View.Helpers.LinkOptions;
type ToolbarOptions = shell.View.Helpers.ToolbarOptions;
type SidebarCardOptions = shell.View.Helpers.SidebarCardOptions;
type GroupHeaderOptions = shell.View.Helpers.GroupHeaderOptions;
type MenuItemSpec = shell.View.Helpers.MenuItemSpec;
type CapabilityDefinitionLike = shell.View.Helpers.CapabilityDefinitionLike;
type CapabilityDetailOptions = shell.View.Helpers.CapabilityDetailOptions;

/**
 * Declared locally rather than aliased off `shell.View.Helpers` because this
 * repo builds against the installed `@punica/editor` (1.13.0), which predates
 * the declaration. Structural typing keeps the exported `Helpers` compatible.
 * Replace both with aliases once this repo installs @punica/editor >= 1.19.0.
 */
interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'filled' | 'outlined' | 'text';
}

interface EmptyStateOptions {
  title: string;
  detail?: string;
  icon?: string;
  tone?: 'muted' | 'warning' | 'error';
  actions?: EmptyStateAction[];
  variant?: 'panel' | 'section';
  fillViewport?: boolean;
  extra?: HTMLElement | HTMLElement[];
}

/**
 * The variants `punica-typography` actually styles — every `:host([variant=…])`
 * rule the component ships, and nothing else. This is the supported set: any
 * other name is dropped rather than written to the DOM.
 *
 * Dropping beats passing through. The component stamps `variant="body1"` on
 * any element created *without* one, so a name it has no rule for is worse
 * than none: it suppresses that default and leaves the text at the inherited
 * size, `inline`, with no `role=heading` and no `aria-level`. Leaving the
 * attribute off lets the component apply `body1` itself.
 *
 * `TypographyOptions.variant` declares the same thirteen, so the type side is
 * covered. This list is the runtime side: extensions reach `createTypography`
 * as compiled JavaScript, where the union is gone and any string can arrive.
 */
const TYPOGRAPHY_VARIANTS = [
  'headline1',
  'headline2',
  'headline3',
  'headline4',
  'headline5',
  'headline6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'button',
  'caption',
  'overline'
] as const;

type TypographyVariant = (typeof TYPOGRAPHY_VARIANTS)[number];

const TYPOGRAPHY_VARIANT_SET: ReadonlySet<string> = new Set(TYPOGRAPHY_VARIANTS);

function isTypographyVariant(value: unknown): value is TypographyVariant {
  return typeof value === 'string' && TYPOGRAPHY_VARIANT_SET.has(value);
}

/**
 * The six weights `punica-typography` ships a `:host([fontweight=…])` rule for.
 *
 * Same reasoning as the variant list above, and the same failure mode: a weight
 * the component has no rule for pins an attribute that matches nothing, so the
 * text renders at the inherited weight while the call site reads as if it asked
 * for something. `normal` is the one that bit us — it was the declared default
 * in `TypographyOptions` and the component never had a rule for it.
 */
const TYPOGRAPHY_WEIGHTS = [
  'thin',
  'regular',
  'medium',
  'semi-bold',
  'bold',
  'extra-bold'
] as const;

type TypographyWeight = (typeof TYPOGRAPHY_WEIGHTS)[number];

const TYPOGRAPHY_WEIGHT_SET: ReadonlySet<string> = new Set(TYPOGRAPHY_WEIGHTS);

function isTypographyWeight(value: unknown): value is TypographyWeight {
  return typeof value === 'string' && TYPOGRAPHY_WEIGHT_SET.has(value);
}

// Generic element creation helper
// Supports both standard HTML elements and custom elements (like punica-*)
function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options?: CreateElementOptions
): HTMLElementTagNameMap[K];
function createElement(
  tag: shell.Components.Element | string,
  options?: CreateElementOptions
): HTMLElement;
function createElement(
  tag: string,
  options?: CreateElementOptions
): HTMLElement {
  const e = document.createElement(tag);

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      e.setAttribute(k, v);
    }
  }

  if (options?.text) {
    e.textContent = options.text;
  }

  if (options?.children) {
    options.children.forEach((child) => e.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      e.setAttribute('style', options.style);
    } else {
      Object.assign(e.style, options.style);
    }
  }

  if (options?.className) {
    e.className = options.className;
  }

  // Last, so a listener can never observe a half-built element.
  if (options?.on) {
    for (const [type, listener] of Object.entries(options.on)) {
      e.addEventListener(type, listener);
    }
  }

  return e;
}

// Punica button creation helper
function createButton(
  label: string,
  onClick: (event: MouseEvent) => void,
  options?: ButtonOptions
): HTMLElement {
  const b = document.createElement('punica-button') as any;

  // Check if label contains HTML tags
  if (label.includes('<') || label.includes('&lt;')) {
    b.innerHTML = label;
  } else {
    b.textContent = label;
  }

  // Default rounded to true unless explicitly false
  if (options?.rounded !== false) {
    b.setAttribute('rounded', '');
  }

  b.setAttribute('size', options?.size ?? 'small');
  b.setAttribute('variant', options?.variant ?? 'outlined');

  if (options?.color) {
    b.setAttribute('color', options.color);
  }

  if (options?.disabled) {
    b.setAttribute('disabled', '');
  }

  if (options?.fullwidth) {
    b.setAttribute('fullwidth', '');
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      b.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      b.setAttribute('style', options.style);
    } else {
      Object.assign(b.style, options.style);
    }
  }

  if (options?.className) {
    b.className = options.className;
  }

  // Add icon if provided
  if (options?.icon) {
    const iconEl = document.createElement('punica-icon');
    iconEl.setAttribute('slot', 'startIcon');
    const i = document.createElement('i');
    i.className = options.icon;
    iconEl.appendChild(i);
    b.appendChild(iconEl);
  }

  // Add click handler only if not disabled
  if (!options?.disabled) {
    b.addEventListener('click', (e: MouseEvent) => onClick(e));
  }

  return b as HTMLElement;
}

// Punica icon button creation helper
function createIconButton(
  iconClass: string,
  onClick: (event: MouseEvent) => void,
  options?: IconButtonOptions
): HTMLElement {
  const btn = document.createElement('punica-icon-button') as any;
  // `x-small` is this API's spelling; `punica-icon-button` only ships a rule
  // for `xsmall`. The hyphen meant the default size matched no
  // `:host([size=…])` rule at all, leaving those buttons at the base padding
  // instead of the 24px box they asked for. Only that one name is translated;
  // anything else is written through untouched.
  const size = options?.size ?? 'x-small';
  btn.setAttribute('size', size === 'x-small' ? 'xsmall' : size);

  if (options?.variant) {
    btn.setAttribute('variant', options.variant);
  }

  if (options?.title) {
    btn.setAttribute('title', options.title);
  }

  if (options?.disabled) {
    btn.setAttribute('disabled', '');
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      btn.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      btn.setAttribute('style', options.style);
    } else {
      Object.assign(btn.style, options.style);
    }
  }

  if (options?.className) {
    btn.className = options.className;
  }

  // A class name is wrapped in an `<i>`; finished markup is written as-is.
  // Same split as `createIcon`, and for the same reason: a toolbar action
  // declares its icon as markup, and wrapping that in another `<i class=…>`
  // would nest it inside an element it was meant to be.
  if (iconClass) {
    btn.innerHTML = `<i class="${iconClass}"></i>`;
  } else if (options?.innerHTML) {
    btn.innerHTML = options.innerHTML;
  }

  // Add click handler only if not disabled
  if (!options?.disabled) {
    btn.addEventListener('click', (e: MouseEvent) => onClick(e));
  }

  return btn as HTMLElement;
}

// Punica typography creation helper
function createTypography(
  text: string,
  options?: TypographyOptions
): HTMLElement {
  const t = document.createElement('punica-typography') as any;
  t.textContent = text;

  // An unstyled variant is worse than none: it pins an attribute the component
  // has no rule for, where leaving it off lets the component apply its own
  // `body1` default. Both entry points are filtered — `attrs` is written last
  // and would otherwise reinstate whatever `variant` was just rejected.
  if (isTypographyVariant(options?.variant)) {
    t.setAttribute('variant', options.variant);
  }

  if (options?.color) {
    t.setAttribute('color', options.color);
  }

  // Only `bold` used to be written, so every other weight the component styles
  // was dropped on the floor and 38 call sites across the fleet went around the
  // helper with `attrs: { fontweight: … }` instead.
  if (isTypographyWeight(options?.fontWeight)) {
    t.setAttribute('fontweight', options.fontWeight);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      if (k === 'variant' && !isTypographyVariant(v)) continue;
      if (k === 'fontweight' && !isTypographyWeight(v)) continue;
      t.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      t.setAttribute('style', options.style);
    } else {
      Object.assign(t.style, options.style);
    }
  }

  if (options?.className) {
    t.className = options.className;
  }

  return t as HTMLElement;
}

// Punica chip creation helper
function createChip(label: string, options?: ChipOptions): HTMLElement {
  const chip = document.createElement('punica-chip') as any;
  chip.setAttribute('size', options?.size ?? 'small');
  chip.setAttribute('variant', options?.variant ?? 'filled');

  if (options?.color) {
    chip.setAttribute('color', options.color);
  }

  // `punica-chip` renders `<slot></slot><slot name="delete"></slot>`, so a
  // chip is icon + label + trailing control, not just a word. It used to be
  // built with `textContent`, which threw both slots away — every caller that
  // needed an icon or a dismiss button had to hand-build the whole chip.
  //
  // A bare label still goes in as text, so the common call is unchanged.
  if (options?.icon || options?.children?.length) {
    if (options.icon) {
      const i = document.createElement('i');
      i.className = options.icon;
      chip.appendChild(i);
    }
    if (label) {
      chip.appendChild(document.createTextNode(label));
    }
    options.children?.forEach((child) => chip.appendChild(child));
  } else {
    chip.textContent = label;
  }

  if (options?.onDelete) {
    const del = document.createElement('button');
    del.setAttribute('slot', 'delete');
    del.setAttribute('type', 'button');
    del.setAttribute('aria-label', 'Remove');
    del.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    del.addEventListener('click', (e) => {
      // The chip itself may be clickable; dismissing is not selecting.
      e.stopPropagation();
      options.onDelete?.(e);
    });
    chip.appendChild(del);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      chip.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      chip.setAttribute('style', options.style);
    } else {
      Object.assign(chip.style, options.style);
    }
  }

  if (options?.className) {
    chip.className = options.className;
  }

  // The component styles itself `cursor: unset` and carries no button
  // semantics, so a chip stays inert until a handler asks for them.
  if (options?.onClick) {
    chip.setAttribute('role', 'button');
    chip.setAttribute('tabindex', '0');
    chip.style.cursor = 'pointer';
    chip.addEventListener('click', (e: Event) => options.onClick?.(e));
    chip.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      options.onClick?.(e);
    });
  }

  return chip as HTMLElement;
}

// Punica box creation helper
function createBox(options?: BoxOptions): HTMLElement {
  const box = document.createElement('punica-box') as any;

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      box.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => box.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      box.setAttribute('style', options.style);
    } else {
      Object.assign(box.style, options.style);
    }
  }

  if (options?.className) {
    box.className = options.className;
  }

  return box as HTMLElement;
}

// Punica row creation helper
function createRow(options?: RowOptions): HTMLElement {
  const row = document.createElement('punica-row') as any;

  if (options?.spacing) {
    row.setAttribute('spacing', options.spacing);
  }

  if (options?.alignItems) {
    row.setAttribute('alignitems', options.alignItems);
  }

  if (options?.wrap) {
    row.setAttribute('wrap', options.wrap);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      row.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => row.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      row.setAttribute('style', options.style);
    } else {
      Object.assign(row.style, options.style);
    }
  }

  if (options?.className) {
    row.className = options.className;
  }

  return row as HTMLElement;
}

// Punica col creation helper
function createCol(options?: ColOptions): HTMLElement {
  const col = document.createElement('punica-col') as any;

  if (options?.xs !== undefined) {
    col.setAttribute('xs', String(options.xs));
  }

  if (options?.sm !== undefined) {
    col.setAttribute('sm', String(options.sm));
  }

  if (options?.md !== undefined) {
    col.setAttribute('md', String(options.md));
  }

  if (options?.lg !== undefined) {
    col.setAttribute('lg', String(options.lg));
  }

  if (options?.xl !== undefined) {
    col.setAttribute('xl', String(options.xl));
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      col.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => col.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      col.setAttribute('style', options.style);
    } else {
      Object.assign(col.style, options.style);
    }
  }

  if (options?.className) {
    col.className = options.className;
  }

  return col as HTMLElement;
}

// ── Secret input marker ─────────────────────────────────────────────────
// A masked field is a credential field, and it used to look like any other
// text input — "API Token" read exactly like "Endpoint URL" until you typed
// into it. Every `type="password"` input now carries the Secret Manager's own
// key glyph, so the field and the panel that stores its value read as one
// thing.
//
// It has to be a light-DOM child: punica-input observes `endadornment` as an
// attribute but its attributeChangedCallback ignores it, so the only adornment
// that renders is a child assigned to the shadow root's
// <slot name="endAdornment">. Extensions that build their credential forms as
// markup write the same element by hand, which is why the stylesheet keys off a
// shared class instead of anything this factory owns.

const SECRET_ADORNMENT_STYLE_ID = 'punica-secret-adornment-styles';

/**
 * Inject the shared adornment stylesheet. Called both from `createInput` and
 * once at shell start (`ViewModule.start`), because an extension's
 * hand-written `<i class="punica-secret-adornment">` must be styled in a
 * session where no `createInput` ever runs.
 */
export function ensureSecretAdornmentStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SECRET_ADORNMENT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SECRET_ADORNMENT_STYLE_ID;
  style.textContent = secretAdornmentCss;
  document.head.appendChild(style);
}

/** The markup extensions copy verbatim — keep the two in sync. */
function createSecretAdornment(): HTMLElement {
  const icon = document.createElement('i');
  icon.className = 'fa-solid fa-key punica-secret-adornment';
  icon.setAttribute('slot', 'endAdornment');
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}

// ── Search input marker ─────────────────────────────────────────────────
// A field you type into to narrow a list carries the magnifier, so "type to
// narrow" reads the same everywhere. Four panels shipped this hand-rolled — the
// marketplace, Courses, ivy-nodes and the ivy-node marketplace, each with its
// own span, its own createIcon call and its own two CSS rules — and this is
// where that convention now lives.
//
// The opt-in is an attribute rather than only an option because `attrs` is the
// one spelling that typechecks against the `@punica/editor` every extension
// already has installed; the typed `search` option is honoured too. On an older
// shell the attribute is inert, so the field renders plain instead of throwing.

const SEARCH_ADORNMENT_STYLE_ID = 'punica-search-adornment-styles';

/** Inject the shared magnifier stylesheet, once per document. */
export function ensureSearchAdornmentStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SEARCH_ADORNMENT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SEARCH_ADORNMENT_STYLE_ID;
  style.textContent = searchAdornmentCss;
  document.head.appendChild(style);
}

/** Duotone, because that is the glyph the four shipped screens render. */
function createSearchAdornment(): HTMLElement {
  const icon = document.createElement('i');
  icon.className =
    'fa-duotone fa-solid fa-magnifying-glass punica-search-adornment';
  icon.setAttribute('slot', 'startAdornment');
  icon.setAttribute('aria-hidden', 'true');
  return icon;
}

// ── Select caret ────────────────────────────────────────────────────────
// `punica-select`'s shadow root is
// `<slot name="adornment"> <div id="contentWrapper"> <slot name="preload">
//  <slot name="clear"> <slot name="caret">` — the caret is the CALLER's to
// supply, and this helper never supplied one. Measured 2026-08-28: a select
// built here is indistinguishable from a text input, and 21 call sites across 9
// extension repos render that way. Exactly one place in the fleet had noticed
// and hand-slotted it (`extension-kernels-runtime/src/ui/sidebarShell.ts:254`,
// raw markup rather than this helper), which is the glyph copied below.
//
// Unconditional, because a select with no affordance is an omission rather than
// a design anyone chose. A caller that wants none removes the child; a caller
// that slots its own does not come through here.

/** The markup the one hand-rolled site renders — keep the two in sync. */
function createSelectCaret(): HTMLElement {
  const caret = document.createElement('punica-icon');
  caret.setAttribute('slot', 'caret');
  caret.setAttribute('aria-hidden', 'true');
  const glyph = document.createElement('i');
  glyph.className = 'fa-light fa-angle-down';
  caret.appendChild(glyph);
  return caret;
}

// Punica input creation helper
function createInput(options?: InputOptions): HTMLElement {
  const input = document.createElement('punica-input') as any;

  if (options?.value !== undefined) {
    input.value = options.value;
  }

  if (options?.placeholder) {
    input.setAttribute('placeholder', options.placeholder);
  }

  if (options?.type) {
    input.setAttribute('type', options.type);
  }

  if (options?.size) {
    input.setAttribute('size', options.size);
  }

  if (options?.fullwidth) {
    input.setAttribute('fullwidth', '');
  }

  if (options?.rounded) {
    input.setAttribute('rounded', '');
  }

  if (options?.disabled) {
    input.setAttribute('disabled', '');
  }

  if (options?.id) {
    input.setAttribute('id', options.id);
  }

  if (options?.min !== undefined) {
    input.setAttribute('min', String(options.min));
  }

  if (options?.max !== undefined) {
    input.setAttribute('max', String(options.max));
  }

  if (options?.step !== undefined) {
    input.setAttribute('step', String(options.step));
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      input.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      input.setAttribute('style', options.style);
    } else {
      Object.assign(input.style, options.style);
    }
  }

  if (options?.className) {
    input.className = options.className;
  }

  // Read the type off the element rather than off `options`: callers reach the
  // same masked field through `attrs: { type: 'password' }`, and both spellings
  // deserve the marker. Slot assignment is order-independent, so appending here
  // does not disturb adornments or children a caller adds afterwards.
  if (input.getAttribute('type') === 'password') {
    ensureSecretAdornmentStyles();
    input.appendChild(createSecretAdornment());
  }

  // Read the attribute off the element as well as the option: `attrs` is how an
  // extension opts in without waiting for a substrate release, and both
  // spellings deserve the glyph. Slot assignment ignores child order, so this
  // sits happily beside the key a masked field would have taken.
  const wantsSearch =
    input.hasAttribute('search') ||
    (options as (InputOptions & { search?: boolean }) | undefined)?.search ===
      true;
  if (wantsSearch) {
    ensureSearchAdornmentStyles();
    input.appendChild(createSearchAdornment());
    if (!input.hasAttribute('autocomplete')) {
      input.setAttribute('autocomplete', 'off');
    }
  }

  // Add event listeners
  if (options?.onInput) {
    input.addEventListener('input', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.value ?? '';
      options.onInput!(value);
    });
  }

  if (options?.onChange) {
    input.addEventListener('change', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.value ?? '';
      options.onChange!(value);
    });
  }

  return input as HTMLElement;
}

// Punica select creation helper
function createSelect(options?: SelectOptions): HTMLElement {
  const select = document.createElement('punica-select') as any;

  if (options?.value !== undefined) {
    select.value = options.value;
  }

  if (options?.size) {
    select.setAttribute('size', options.size);
  }

  if (options?.fullwidth) {
    select.setAttribute('fullwidth', '');
  }

  if (options?.rounded) {
    select.setAttribute('rounded', '');
  }

  if (options?.disabled) {
    select.setAttribute('disabled', '');
  }

  if (options?.minimumTargetWidth) {
    select.setAttribute('minimumtargetwidth', '');
  }

  if (options?.id) {
    select.setAttribute('id', options.id);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      select.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      select.setAttribute('style', options.style);
    } else {
      Object.assign(select.style, options.style);
    }
  }

  if (options?.className) {
    select.className = options.className;
  }

  // Add change event listener
  if (options?.onChange) {
    select.addEventListener('change', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.value ?? '';
      options.onChange!(value);
    });
  }

  // The caret goes in before the caller appends its items. Slotted content is
  // positioned by the component, so light-DOM order does not matter, and the
  // element is a `punica-icon` rather than a `punica-select-item`, so an item
  // query cannot pick it up.
  select.appendChild(createSelectCaret());

  return select as HTMLElement;
}

// Punica select item creation helper
function createSelectItem(options: SelectItemOptions): HTMLElement {
  const item = document.createElement('punica-select-item') as any;

  item.setAttribute('value', options.value);

  if (options.text) {
    item.textContent = options.text;
  }

  if (options.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      item.setAttribute(k, v);
    }
  }

  return item as HTMLElement;
}

// Punica checkbox creation helper
function createCheckbox(options?: CheckboxOptions): HTMLElement {
  const checkbox = document.createElement('punica-checkbox') as any;

  if (options?.checked !== undefined) {
    checkbox.checked = options.checked;
  }

  if (options?.disabled) {
    checkbox.setAttribute('disabled', '');
  }

  if (options?.id) {
    checkbox.setAttribute('id', options.id);
  }

  if (options?.label) {
    checkbox.setAttribute('label', options.label);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      checkbox.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      checkbox.setAttribute('style', options.style);
    } else {
      Object.assign(checkbox.style, options.style);
    }
  }

  if (options?.className) {
    checkbox.className = options.className;
  }

  // Add change event listener
  if (options?.onChange) {
    checkbox.addEventListener('change', (e: any) => {
      const checked = e?.detail?.checked ?? e?.target?.checked ?? false;
      options.onChange!(checked);
    });
  }

  return checkbox as HTMLElement;
}

// Punica icon creation helper
function createIcon(options?: IconOptions): HTMLElement {
  const icon = document.createElement('punica-icon') as any;

  if (options?.iconClass) {
    const i = document.createElement('i');
    i.className = options.iconClass;
    icon.appendChild(i);
  } else if (options?.innerHTML) {
    icon.innerHTML = options.innerHTML;
  }

  if (options?.slot) {
    icon.setAttribute('slot', options.slot);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      icon.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      icon.setAttribute('style', options.style);
    } else {
      Object.assign(icon.style, options.style);
    }
  }

  if (options?.className) {
    icon.className = options.className;
  }

  return icon as HTMLElement;
}

// Punica textarea creation helper
function createTextarea(options?: TextareaOptions): HTMLElement {
  const textarea = document.createElement('punica-textarea') as any;

  if (options?.value !== undefined) {
    textarea.value = options.value;
  }

  if (options?.placeholder) {
    textarea.setAttribute('placeholder', options.placeholder);
  }

  if (options?.rows) {
    textarea.setAttribute('rows', String(options.rows));
  }

  if (options?.cols) {
    textarea.setAttribute('cols', String(options.cols));
  }

  if (options?.size) {
    textarea.setAttribute('size', options.size);
  }

  if (options?.fullwidth) {
    textarea.setAttribute('fullwidth', '');
  }

  if (options?.rounded) {
    textarea.setAttribute('rounded', '');
  }

  if (options?.disabled) {
    textarea.setAttribute('disabled', '');
  }

  if (options?.id) {
    textarea.setAttribute('id', options.id);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      textarea.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      textarea.setAttribute('style', options.style);
    } else {
      Object.assign(textarea.style, options.style);
    }
  }

  if (options?.className) {
    textarea.className = options.className;
  }

  // Add event listeners
  if (options?.onInput) {
    textarea.addEventListener('input', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.value ?? '';
      options.onInput!(value);
    });
  }

  if (options?.onChange) {
    textarea.addEventListener('change', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.value ?? '';
      options.onChange!(value);
    });
  }

  return textarea as HTMLElement;
}

// Punica divider creation helper
function createDivider(options?: DividerOptions): HTMLElement {
  const divider = document.createElement('punica-divider') as any;

  if (options?.orientation) {
    divider.setAttribute('orientation', options.orientation);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      divider.setAttribute(k, v);
    }
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      divider.setAttribute('style', options.style);
    } else {
      Object.assign(divider.style, options.style);
    }
  }

  if (options?.className) {
    divider.className = options.className;
  }

  return divider as HTMLElement;
}

// Punica list view creation helper
function createListView(options?: ListViewOptions): HTMLElement {
  const list = document.createElement('punica-list-view') as any;

  if (options?.enableDivider) {
    list.setAttribute('enabledivider', '');
  }

  if (options?.spacing) {
    list.setAttribute('spacing', options.spacing);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      list.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => list.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      list.setAttribute('style', options.style);
    } else {
      Object.assign(list.style, options.style);
    }
  }

  if (options?.className) {
    list.className = options.className;
  }

  return list as HTMLElement;
}

// ── List view item standard ─────────────────────────────────────────────
// punica-list-view-item's shadow root is a single UNNAMED <slot>, so children
// carrying slot="label" / "content" / "endAdornment" were assigned to nothing
// and every row rendered blank. Named slots are only matched by a <slot name=…>
// and the component has none, so the three regions live in light DOM instead.
//
// The custom element stays as the wrapper regardless: punica-list-view draws
// its separators with ::slotted(punica-list-view-item[data-divider]), so
// swapping in a <div> would silently drop every divider. Styling is its own
// guarded <style>, deliberately separate from card.css — that sheet is shared
// by every createSidebarCard consumer and must not gain new coupling.

const LIST_ITEM_STYLE_ID = 'punica-list-view-item-styles';

function ensureListItemStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(LIST_ITEM_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = LIST_ITEM_STYLE_ID;
  style.textContent = listItemCss;
  document.head.appendChild(style);
}

// Punica list view item creation helper
function createListViewItem(options?: ListViewItemOptions): HTMLElement {
  ensureListItemStyles();
  const item = document.createElement('punica-list-view-item') as any;

  const row = document.createElement('div');
  row.className = 'punica-list-item-row';

  const main = document.createElement('div');
  main.className = 'punica-list-item-main';

  if (options?.label) {
    if (typeof options.label === 'string') {
      // Plain div (not punica-typography) so .punica-list-item-label controls
      // the size — same reason createSidebarCard avoids it for .punica-card-name.
      const label = document.createElement('div');
      label.className = 'punica-list-item-label';
      label.textContent = options.label;
      main.appendChild(label);
    } else {
      // Caller-built element: strip the slot hint written against the old
      // contract so existing callers render without needing a change.
      options.label.removeAttribute('slot');
      main.appendChild(options.label);
    }
  }

  if (options?.content) {
    if (typeof options.content === 'string') {
      const content = document.createElement('div');
      content.className = 'punica-list-item-content';
      content.textContent = options.content;
      main.appendChild(content);
    } else {
      options.content.removeAttribute('slot');
      main.appendChild(options.content);
    }
  }

  if (main.childNodes.length > 0) {
    row.appendChild(main);
  }

  if (options?.endAdornment) {
    options.endAdornment.removeAttribute('slot');
    const end = document.createElement('div');
    end.className = 'punica-list-item-end';
    end.appendChild(options.endAdornment);
    row.appendChild(end);
  }

  item.appendChild(row);

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      item.setAttribute(k, v);
    }
  }

  // Unchanged: extra children go straight on the host element, where the
  // unnamed slot picks them up. This was the only path that rendered before.
  if (options?.children) {
    options.children.forEach((child) => item.appendChild(child));
  }

  return item as HTMLElement;
}

// Punica paper creation helper
function createPaper(options?: PaperOptions): HTMLElement {
  const paper = document.createElement('punica-paper') as any;

  if (options?.elevation !== undefined) {
    paper.setAttribute('elevation', String(options.elevation));
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      paper.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => paper.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      paper.setAttribute('style', options.style);
    } else {
      Object.assign(paper.style, options.style);
    }
  }

  if (options?.className) {
    paper.className = options.className;
  }

  return paper as HTMLElement;
}

// Punica tab creation helper
function createTab(options?: TabOptions): HTMLElement {
  const tab = document.createElement('punica-tab') as any;

  if (options?.value) {
    tab.setAttribute('value', options.value);
  }

  if (options?.orientation) {
    tab.setAttribute('orientation', options.orientation);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      tab.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => tab.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      tab.setAttribute('style', options.style);
    } else {
      Object.assign(tab.style, options.style);
    }
  }

  if (options?.className) {
    tab.className = options.className;
  }

  // Add change event listener
  if (options?.onChange) {
    tab.addEventListener('change', (e: any) => {
      const value = e?.detail?.value ?? e?.target?.getAttribute('value') ?? '';
      options.onChange!(value);
    });
  }

  return tab as HTMLElement;
}

// Punica tab item creation helper
function createTabItem(options: TabItemOptions): HTMLElement {
  const item = document.createElement('punica-tab-item') as any;

  item.setAttribute('value', options.value);

  if (options.text) {
    item.textContent = options.text;
  }

  if (options.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      item.setAttribute(k, v);
    }
  }

  if (options.children) {
    options.children.forEach((child) => item.appendChild(child));
  }

  if (options.style) {
    if (typeof options.style === 'string') {
      item.setAttribute('style', options.style);
    } else {
      Object.assign(item.style, options.style);
    }
  }

  if (options.className) {
    item.className = options.className;
  }

  return item as HTMLElement;
}

// Punica tab panel creation helper
function createTabPanel(options: TabPanelOptions): HTMLElement {
  const panel = document.createElement('punica-tab-panel') as any;

  panel.setAttribute('value', options.value);

  if (options.selectedValue) {
    panel.setAttribute('selectedvalue', options.selectedValue);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      panel.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => panel.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      panel.setAttribute('style', options.style);
    } else {
      Object.assign(panel.style, options.style);
    }
  }

  if (options?.className) {
    panel.className = options.className;
  }

  return panel as HTMLElement;
}

// Punica badge creation helper
function createBadge(options?: BadgeOptions): HTMLElement {
  const badge = document.createElement('punica-badge') as any;

  if (options?.text) {
    badge.textContent = options.text;
  }

  if (options?.variant) {
    badge.setAttribute('variant', options.variant);
  }

  if (options?.color) {
    badge.setAttribute('color', options.color);
  }

  if (options?.size) {
    badge.setAttribute('size', options.size);
  }

  if (options?.title) {
    badge.setAttribute('title', options.title);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      badge.setAttribute(k, v);
    }
  }

  return badge as HTMLElement;
}

// Punica menu creation helper
function createMenu(options?: MenuOptions): HTMLElement {
  const menu = document.createElement('punica-menu') as any;

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      menu.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => menu.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      menu.setAttribute('style', options.style);
    } else {
      Object.assign(menu.style, options.style);
    }
  }

  if (options?.className) {
    menu.className = options.className;
  }

  return menu as HTMLElement;
}

// Punica menu item creation helper
function createMenuItem(options?: MenuItemOptions): HTMLElement {
  const item = document.createElement('punica-menu-item') as any;

  // Written before `text`/`children` so it is the row's ground, not a wipe of
  // whatever they added. `text` still wins when both are given.
  if (options?.innerHTML) {
    item.innerHTML = options.innerHTML;
  }

  if (options?.text) {
    const text = createTypography(options.text);
    item.appendChild(text);
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      item.setAttribute(k, v);
    }
  }

  if (options?.onClick) {
    item.addEventListener('click', options.onClick);
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      item.setAttribute('style', options.style);
    } else {
      Object.assign(item.style, options.style);
    }
  }

  if (options?.className) {
    item.className = options.className;
  }

  if (options?.children) {
    options.children.forEach((child) => item.appendChild(child));
  }

  return item as HTMLElement;
}

/**
 * Open a `punica-menu` beside something.
 *
 * The component already does the hard parts — backdrop, Escape, theme, and
 * flipping when the menu does not fit — but it reads its position off
 * `top`/`left`/`width`/`height`/`bottom`/`placement`, which `createMenu` never
 * exposed. So every caller measured the anchor and wrote those attributes by
 * hand: four places in this shell alone, and fifteen more across the
 * extensions. This is that block, once.
 *
 * The anchor is an element for the usual case (a button opens its menu) or a
 * bare rect for the cursor-driven one — a context menu opens at the pointer,
 * which has no element to measure.
 */
function openMenuAt(menu: HTMLElement, options: MenuAnchorOptions): void {
  const a = options.anchor;
  const rect =
    typeof (a as HTMLElement).getBoundingClientRect === 'function'
      ? (a as HTMLElement).getBoundingClientRect()
      : (a as { top: number; left: number; bottom?: number });

  const bottom = (rect as DOMRect).bottom ?? rect.top;
  const width = (rect as DOMRect).width;
  const height = (rect as DOMRect).height;

  menu.setAttribute('top', String(rect.top));
  menu.setAttribute('left', String(rect.left));
  // A rect anchor may carry no size; the component treats the missing pair as
  // a zero-size point, which is what a cursor is.
  menu.setAttribute('width', String(width ?? 0));
  menu.setAttribute('height', String(height ?? 0));
  menu.setAttribute('bottom', String(bottom));
  menu.setAttribute('placement', options.placement ?? 'bottom');
  menu.setAttribute('open', 'true');
}

/**
 * Close an open menu. The component fires `close` as it hides, so a caller
 * that syncs state off that event keeps working.
 */
function closeMenu(menu: HTMLElement): void {
  menu.removeAttribute('open');
}

// Punica split (resizable pane container) creation helper
function createSplit(options?: SplitOptions): HTMLElement {
  const split = document.createElement('punica-split') as any;

  if (options?.orientation) {
    split.setAttribute('orientation', options.orientation);
  }

  // The component reads pane sizes as a comma-joined percentage list.
  if (options?.sizes?.length) {
    split.setAttribute('sizes', options.sizes.join(','));
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      split.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => split.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      split.setAttribute('style', options.style);
    } else {
      Object.assign(split.style, options.style);
    }
  }

  if (options?.className) {
    split.className = options.className;
  }

  return split as HTMLElement;
}

/**
 * A right-click menu whose items never change.
 *
 * `attachContextMenu` is the other half of this pair: it rebuilds its items on
 * every right-click from the row under the cursor, which is what a list view
 * needs and what a fixed menu pays for in indirection. This one builds the
 * items once and reports the chosen value; the caller decides what it applies
 * to (usually by tracking its own `contextmenu` on the rows).
 */
function createContextMenu(options: ContextMenuOptions): HTMLElement {
  const menu = document.createElement('punica-context-menu') as any;

  if (options.target) {
    menu.setAttribute('target', options.target);
  }

  if (options.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      menu.setAttribute(k, v);
    }
  }

  for (const item of options.items) {
    if (item.separatorBefore) {
      menu.appendChild(document.createElement('punica-context-menu-separator'));
    }
    const el = document.createElement('punica-context-menu-item');
    el.setAttribute('label', item.label);
    el.setAttribute('value', item.value);
    if (item.icon) el.setAttribute('icon', item.icon);
    menu.appendChild(el);
  }

  menu.addEventListener('punica-context-menu-select', (e: Event) => {
    const value = (e as CustomEvent)?.detail?.value as string | undefined;
    if (value) options.onSelect(value);
  });

  return menu as HTMLElement;
}

// Link (anchor) creation helper
const LINK_STYLE_ID = 'punica-link-styles';

function ensureLinkStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(LINK_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = LINK_STYLE_ID;
  style.textContent = linkCss;
  document.head.appendChild(style);
}

function createLink(text: string, options: LinkOptions): HTMLElement {
  ensureLinkStyles();
  const link = document.createElement('a');
  link.href = options.href;
  link.textContent = text;

  if (options.target) {
    link.target = options.target;
  }

  if (options.rel) {
    link.rel = options.rel;
  }

  if (options.download) {
    link.download = options.download;
  }

  if (options.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      link.setAttribute(k, v);
    }
  }

  if (options.children) {
    options.children.forEach((child) => link.appendChild(child));
  }

  if (options.style) {
    if (typeof options.style === 'string') {
      link.setAttribute('style', options.style);
    } else {
      Object.assign(link.style, options.style);
    }
  }

  if (options.className) {
    link.className = options.className;
  }

  // After the className assignment, not before: that line ASSIGNS rather than
  // appends, so an earlier add is wiped — and the caller that surfaced this
  // defect (extension-settings' llama.cpp link) passes one. A caller's inline
  // `style` still outranks the sheet, which is how extension-viewer keeps its
  // own colour.
  link.classList.add('punica-link');

  return link;
}

// Punica toolbar creation helper
function createToolbar(options?: ToolbarOptions): HTMLElement {
  const toolbar = document.createElement('punica-toolbar') as any;

  if (options?.variant) {
    toolbar.setAttribute('variant', options.variant);
  }

  if (options?.disableGutters) {
    toolbar.setAttribute('disablegutters', '');
  }

  if (options?.attrs) {
    for (const [k, v] of Object.entries(options.attrs)) {
      toolbar.setAttribute(k, v);
    }
  }

  if (options?.children) {
    options.children.forEach((child) => toolbar.appendChild(child));
  }

  if (options?.style) {
    if (typeof options.style === 'string') {
      toolbar.setAttribute('style', options.style);
    } else {
      Object.assign(toolbar.style, options.style);
    }
  }

  if (options?.className) {
    toolbar.className = options.className;
  }

  return toolbar as HTMLElement;
}

// ── Sidebar card standard (shared) ──────────────────────────────────────
// One card design for every list-based sidebar/panel view: a status dot or
// type icon, a bold title over a muted subtitle, optional right-aligned meta,
// and no inline buttons — per-row actions live in a right-click context menu
// (attachContextMenu). Styling is injected once as a guarded <style> so every
// consumer looks identical without shipping its own CSS. Theme-aware vars
// only, so light and dark both read correctly.

const CARD_STYLE_ID = 'punica-card-styles';

function ensureCardStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CARD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CARD_STYLE_ID;
  style.textContent = cardCss;
  document.head.appendChild(style);
}

function createSidebarCard(options: SidebarCardOptions): HTMLElement {
  ensureCardStyles();
  const card = document.createElement('div');
  let cls = 'punica-card';
  if (options.inactive) cls += ' punica-card--inactive';
  if (options.selected) cls += ' punica-card--selected';
  card.className = cls;
  card.dataset.nodeId = options.id;
  if (options.data) {
    for (const [k, v] of Object.entries(options.data)) card.dataset[k] = v;
  }
  if (options.title) card.title = options.title;

  // Left glyph: status dot, else type icon, else nothing. The dot is the shared
  // one (statusDot module) — static here, since a list of rows all pulsing at
  // once reads as noise; the status bar opts into the pulse instead.
  if (options.dot) {
    card.appendChild(createStatusDot(options.dot));
  } else if (options.icon) {
    const iconWrap = document.createElement('span');
    iconWrap.className = 'punica-card-icon';
    const i = document.createElement('i');
    i.className = options.icon;
    iconWrap.appendChild(i);
    card.appendChild(iconWrap);
  }

  const main = document.createElement('div');
  main.className = 'punica-card-main';
  // Plain div (not createTypography) so .punica-card-name controls the size —
  // the punica-typography component sizes its own shadow text and ignores the
  // host class, rendering the title oversized.
  const name = document.createElement('div');
  name.className = 'punica-card-name';
  name.textContent = options.name;
  main.appendChild(name);
  if (options.sub) {
    const sub = document.createElement('div');
    sub.className = 'punica-card-sub';
    sub.textContent = options.sub;
    main.appendChild(sub);
  }
  card.appendChild(main);

  if (options.meta) {
    const meta = document.createElement('span');
    meta.className = 'punica-card-meta';
    meta.textContent = options.meta;
    card.appendChild(meta);
  }
  return card;
}

/**
 * Humanize a byte count for display.
 *
 * Shared rather than re-implemented per panel: the same dataset was printed as
 * "6602" in a search list and "6.4 KB" in the local list, which reads as two
 * different sizes. Null for absent/zero/negative so a caller can drop the chip
 * instead of showing "0 B" for an unknown size.
 */
function formatBytes(bytes?: number | null): string | null {
  if (!bytes || bytes <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  // One decimal only where it carries information: "1.5 KB", but "1 KB" not
  // "1.0 KB", "12 MB" not "12.3 MB", and never a fraction of a byte.
  const text =
    value >= 10 || i === 0
      ? String(Math.round(value))
      : value.toFixed(1).replace(/\.0$/, '');
  return `${text} ${units[i]}`;
}

function createGroupHeader(
  options: GroupHeaderOptions | string
): HTMLElement {
  ensureCardStyles();
  const label = typeof options === 'string' ? options : options.label;
  const count = typeof options === 'string' ? undefined : options.count;
  const header = document.createElement('div');
  header.className = 'punica-group-header';
  header.textContent = count ? `${label} · ${count}` : label;
  return header;
}

// ── Empty states ─────────────────────────────────────────────────────────────
// One no-data look for every panel. Before this existed, 44 extension repos had
// each written their own `*-empty*` class: some left-aligned at the top of a
// tall blank area, some centred, one with a dashed border, and eight had
// separately re-derived the same `calc(100vh - …)` because the ancestor
// `punica-col`s do not propagate a height.
//
// Two variants, because the situations are not the same shape:
//   panel   — the whole view has nothing. Icon, bold title, one muted sentence,
//             stacked buttons, centred on both axes.
//   section — one section of a multi-section panel, or a popover list. A single
//             centred muted line; an icon here would put two large glyphs side
//             by side the moment two sections empty at once.
// Both work with no icon and no actions: the structure is additive, so a panel
// that has nothing to offer but a sentence still centres correctly.

const EMPTY_STATE_STYLE_ID = 'punica-empty-state-styles';

function ensureEmptyStateStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(EMPTY_STATE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = EMPTY_STATE_STYLE_ID;
  style.textContent = emptyStateCss;
  document.head.appendChild(style);
}

function createEmptyState(options: EmptyStateOptions): HTMLElement {
  ensureEmptyStateStyles();
  const variant = options.variant ?? 'panel';

  const box = document.createElement('div');
  box.className = `punica-empty-state punica-empty-state--${variant}`;
  // `min-height: 100%` needs an ancestor with a definite height. Where the
  // column chain does not provide one the state would sit at the top instead,
  // silently — this is the opt-out, and it is what the eight hand-rolled
  // viewport calcs were doing.
  if (options.fillViewport) box.classList.add('punica-empty-state--fill');

  // The icon belongs to the panel variant only (see the note above).
  if (options.icon && variant === 'panel') {
    const iconWrap = document.createElement('div');
    const tone = options.tone ?? 'muted';
    iconWrap.className =
      tone === 'muted'
        ? 'punica-empty-state-icon'
        : `punica-empty-state-icon punica-empty-state-icon--${tone}`;
    const i = document.createElement('i');
    i.className = options.icon;
    iconWrap.appendChild(i);
    box.appendChild(iconWrap);
  }

  // Plain divs, not createTypography: the component sizes its own shadow text
  // and ignores the host class, which renders the title oversized (the same
  // reason createSidebarCard builds its name by hand).
  const title = document.createElement('div');
  title.className = 'punica-empty-state-title';
  title.textContent = options.title;
  box.appendChild(title);

  if (options.detail) {
    const detail = document.createElement('div');
    detail.className = 'punica-empty-state-detail';
    detail.textContent = options.detail;
    box.appendChild(detail);
  }

  // No actions key → no row at all, rather than an empty flex box adding its
  // margin to the gap below the text.
  if (options.actions && options.actions.length > 0) {
    const actions = document.createElement('div');
    actions.className = 'punica-empty-state-actions';
    options.actions.forEach((action, index) => {
      // The first action is the one to take; the rest are alternatives.
      const variant = action.variant ?? (index === 0 ? 'filled' : 'outlined');
      actions.appendChild(
        createButton(action.label, () => action.onClick(), {
          variant,
          // `filled` with no `color` renders TRANSPARENT: punica-button's last
          // unscoped `:host([variant='filled'])` rule wins and sets
          // `background-color: transparent`, so the button reads as plain text.
          // Measured over CDP on 2026-08-27; docker's own Retry button had been
          // invisible this way for as long as it existed.
          ...(variant === 'filled' ? { color: 'primary' as const } : {}),
          size: 'small'
        })
      );
    });
    box.appendChild(actions);
  }

  // Anything the two text lines cannot carry — a code snippet to paste, a
  // second row of chips. Inside the centred column, so a caller never has to
  // append a sibling and fight `min-height: 100%` for the space.
  if (options.extra) {
    const extras = Array.isArray(options.extra) ? options.extra : [options.extra];
    for (const extra of extras) box.appendChild(extra);
  }

  return box;
}

function attachContextMenu(opts: {
  listEl: HTMLElement;
  menuId: string;
  buildItems: (row: HTMLElement) => MenuItemSpec[];
  onSelect: (value: string, row: HTMLElement) => void;
}): () => void {
  if (typeof document === 'undefined') return () => {};
  const menu = document.createElement('punica-context-menu');
  menu.setAttribute('id', opts.menuId);
  if (opts.listEl.id) menu.setAttribute('target', `#${opts.listEl.id}`);

  let currentRow: HTMLElement | null = null;

  const onContext = (ev: Event) => {
    const target = (ev as MouseEvent).target as HTMLElement;
    const row = target.closest('[data-node-id]') as HTMLElement | null;
    currentRow = row;
    menu.innerHTML = '';
    if (!row) return;
    for (const item of opts.buildItems(row)) {
      if (item.separatorBefore) {
        menu.appendChild(
          document.createElement('punica-context-menu-separator')
        );
      }
      const el = document.createElement('punica-context-menu-item');
      el.setAttribute('label', item.label);
      el.setAttribute('value', item.value);
      if (item.icon) el.setAttribute('icon', item.icon);
      menu.appendChild(el);
    }
  };

  const onSelectEvt = (e: Event) => {
    const value = (e as CustomEvent)?.detail?.value as string | undefined;
    if (!value || !currentRow) return;
    opts.onSelect(value, currentRow);
  };

  opts.listEl.addEventListener('contextmenu', onContext);
  menu.addEventListener('punica-context-menu-select', onSelectEvt);
  opts.listEl.insertAdjacentElement('afterend', menu);

  return () => {
    opts.listEl.removeEventListener('contextmenu', onContext);
    menu.removeEventListener('punica-context-menu-select', onSelectEvt);
    menu.remove();
  };
}

// ── Capability detail standard (shared) ────────────────────────────────
// One renderer for every surface that shows a capability (extension viewer,
// llms viewer, ivy-node explorer, capability catalog): bold title + mono id,
// risk/approval pills, a source·id·version·approval meta line, Inputs/Outputs
// schema blocks, Tags, and a collapsed raw-JSON toggle. Optionally wrapped in
// a click-to-expand row for list contexts. CSS injected once (theme-aware),
// mirroring the sidebar-card pattern above.

const CAPABILITY_STYLE_ID = 'punica-capability-styles';

function ensureCapabilityStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CAPABILITY_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CAPABILITY_STYLE_ID;
  style.textContent = capabilityCss;
  document.head.appendChild(style);
}

// Syntax-highlighted JSON <pre> — ported so consumers need no local copy.
function jsonAppendSpan(
  parent: HTMLElement,
  text: string,
  kind: 'key' | 'string' | 'number' | 'boolean' | 'null'
): void {
  const span = document.createElement('span');
  span.className = `json-view-${kind}`;
  span.textContent = text;
  parent.appendChild(span);
}

function jsonAppendValue(parent: HTMLElement, value: unknown, indent: number): void {
  const pad = '  '.repeat(indent);
  if (value === null) return jsonAppendSpan(parent, 'null', 'null');
  if (typeof value === 'boolean') return jsonAppendSpan(parent, String(value), 'boolean');
  if (typeof value === 'number') return jsonAppendSpan(parent, String(value), 'number');
  if (typeof value === 'string') return jsonAppendSpan(parent, JSON.stringify(value), 'string');
  if (Array.isArray(value)) {
    parent.appendChild(document.createTextNode('[\n'));
    value.forEach((item, i) => {
      parent.appendChild(document.createTextNode(`${pad}  `));
      jsonAppendValue(parent, item, indent + 1);
      if (i < value.length - 1) parent.appendChild(document.createTextNode(','));
      parent.appendChild(document.createTextNode('\n'));
    });
    parent.appendChild(document.createTextNode(`${pad}]`));
    return;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    parent.appendChild(document.createTextNode('{\n'));
    keys.forEach((key, i) => {
      parent.appendChild(document.createTextNode(`${pad}  `));
      jsonAppendSpan(parent, JSON.stringify(key), 'key');
      parent.appendChild(document.createTextNode(': '));
      jsonAppendValue(parent, (value as Record<string, unknown>)[key], indent + 1);
      if (i < keys.length - 1) parent.appendChild(document.createTextNode(','));
      parent.appendChild(document.createTextNode('\n'));
    });
    parent.appendChild(document.createTextNode(`${pad}}`));
  }
}

function renderCapabilityJson(value: unknown): HTMLElement {
  const pre = document.createElement('pre');
  pre.className = 'json-view cap-schema-code';
  jsonAppendValue(pre, value, 0);
  return pre;
}

function capSectionLabel(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'cap-detail-section';
  el.textContent = text;
  return el;
}

function capMetaNote(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'cap-detail-meta cap-detail-note';
  el.textContent = text;
  return el;
}

function capPill(text: string, kind: string): HTMLElement {
  const pill = document.createElement('span');
  pill.className = `cap-pill cap-pill-${kind}`;
  pill.textContent = text;
  return pill;
}

// Fill `detail` with the meta line, Inputs, Outputs, Tags, extra sections and
// the collapsed raw-definition toggle — the expanded portion of the card.
function buildCapabilityBody(
  detail: HTMLElement,
  def: CapabilityDefinitionLike,
  opts: CapabilityDetailOptions
): void {
  // Meta line — grounds even a parameterless capability so it never looks empty.
  const metaParts: string[] = [`source: ${opts.source || 'unknown'}`];
  if (def.id) metaParts.push(`id: ${def.id}`);
  if (def.version) metaParts.push(`version: ${def.version}`);
  const approval = def.policy?.approval;
  if (approval) metaParts.push(`approval: ${approval}`);
  if (def.sideEffects && def.sideEffects.length > 0) {
    metaParts.push(`sideEffects: ${def.sideEffects.join(', ')}`);
  }
  const metaLine = document.createElement('div');
  metaLine.className = 'cap-detail-meta';
  metaLine.textContent = metaParts.join('  ·  ');
  detail.appendChild(metaLine);

  // Inputs — schema when declared, else an explicit "no parameters" note.
  detail.appendChild(capSectionLabel('Inputs'));
  if (def.inputs?.schema) {
    detail.appendChild(renderCapabilityJson(def.inputs.schema));
  } else {
    detail.appendChild(capMetaNote('This capability takes no input parameters.'));
  }

  // Outputs — describe the delivery mechanism, then the payload schema.
  detail.appendChild(capSectionLabel('Outputs'));
  if (def.outputs) {
    const o = def.outputs;
    if (o.kind === 'event') {
      const parts: string[] = [];
      if (o.resultEvent) parts.push(`success → ${o.resultEvent}`);
      if (o.errorEvent) parts.push(`error → ${o.errorEvent}`);
      const line = document.createElement('div');
      line.className = 'cap-detail-meta';
      line.textContent = parts.length
        ? `Emits an event on completion (${parts.join('  ·  ')}).`
        : 'Emits an event on completion.';
      detail.appendChild(line);
    } else if (o.kind === 'direct') {
      detail.appendChild(capMetaNote('Returns a value directly to the caller.'));
    } else if (o.kind) {
      const line = document.createElement('div');
      line.className = 'cap-detail-meta';
      line.textContent = `kind: ${o.kind}`;
      detail.appendChild(line);
    }
    if (o.schema) {
      detail.appendChild(capSectionLabel('Result payload'));
      detail.appendChild(renderCapabilityJson(o.schema));
    } else {
      detail.appendChild(capMetaNote('Result payload shape is not documented.'));
    }
  } else {
    detail.appendChild(capMetaNote('No outputs declared.'));
  }

  if (def.tags && def.tags.length > 0) {
    detail.appendChild(capSectionLabel('Tags'));
    const tags = document.createElement('div');
    tags.className = 'cap-detail-tags';
    for (const t of def.tags) {
      const chip = document.createElement('span');
      chip.className = 'cap-tag';
      chip.textContent = t;
      tags.appendChild(chip);
    }
    detail.appendChild(tags);
  }

  // Consumer-specific sections (e.g. an instruction class's Execution Defaults).
  if (opts.extraSections) {
    for (const section of opts.extraSections) detail.appendChild(section);
  }

  // Raw definition — a catch-all so nothing the manifest declares is hidden.
  const rawToggle = document.createElement('button');
  rawToggle.type = 'button';
  rawToggle.className = 'cap-detail-raw-toggle';
  rawToggle.textContent = 'Show raw definition';
  const rawBlock = renderCapabilityJson(def);
  rawBlock.style.display = 'none';
  rawToggle.addEventListener('click', () => {
    const open = rawBlock.style.display === 'none';
    rawBlock.style.display = open ? 'block' : 'none';
    rawToggle.textContent = open ? 'Hide raw definition' : 'Show raw definition';
  });
  detail.appendChild(capSectionLabel('Raw'));
  detail.appendChild(rawToggle);
  detail.appendChild(rawBlock);
}

function createCapabilityDetail(
  def: CapabilityDefinitionLike,
  options?: CapabilityDetailOptions
): HTMLElement {
  ensureCapabilityStyles();
  const opts = options ?? {};
  const collapsible = opts.collapsible === true;

  const row = document.createElement('div');
  row.className = collapsible ? 'cap-row' : 'cap-row cap-row--static';

  const head = document.createElement('div');
  head.className = 'cap-row-head';
  if (collapsible) {
    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    const chevron = document.createElement('i');
    chevron.className = 'cap-row-chevron fa-solid fa-chevron-right';
    head.appendChild(chevron);
  }

  const main = document.createElement('div');
  main.className = 'cap-row-main';
  const titleLine = document.createElement('div');
  titleLine.className = 'cap-row-title-line';
  const title = document.createElement('span');
  title.className = 'cap-row-title';
  title.textContent = def.title || def.id || '(untitled)';
  titleLine.appendChild(title);
  if (def.id) {
    const id = document.createElement('span');
    id.className = 'cap-row-id';
    id.textContent = def.id;
    titleLine.appendChild(id);
  }
  main.appendChild(titleLine);
  if (def.description) {
    const desc = document.createElement('div');
    desc.className = 'cap-row-desc';
    desc.textContent = def.description;
    main.appendChild(desc);
  }
  head.appendChild(main);

  const badges = document.createElement('div');
  badges.className = 'cap-row-badges';
  const risk = def.policy?.risk;
  if (risk) badges.appendChild(capPill(risk, `risk-${risk}`));
  const approval = def.policy?.approval;
  if (approval && approval !== 'none') badges.appendChild(capPill(approval, 'approval'));
  head.appendChild(badges);
  row.appendChild(head);

  const detail = document.createElement('div');
  detail.className = 'cap-row-detail';
  row.appendChild(detail);

  if (collapsible) {
    detail.style.display = opts.open ? 'block' : 'none';
    let built = false;
    const ensureBuilt = (): void => {
      if (built) return;
      built = true;
      buildCapabilityBody(detail, def, opts);
    };
    if (opts.open) {
      ensureBuilt();
      row.classList.add('expanded');
    }
    const toggle = (): void => {
      const open = detail.style.display === 'none';
      if (open) ensureBuilt();
      detail.style.display = open ? 'block' : 'none';
      row.classList.toggle('expanded', open);
    };
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        toggle();
      }
    });
  } else {
    buildCapabilityBody(detail, def, opts);
  }

  return row;
}

export const Helpers = {
  createElement,
  createSidebarCard,
  createStatusDot,
  createGroupHeader,
  createEmptyState,
  formatBytes,
  attachContextMenu,
  createCapabilityDetail,
  createButton,
  createIconButton,
  createTypography,
  createChip,
  createBox,
  createRow,
  createCol,
  createInput,
  createSelect,
  createSelectItem,
  createCheckbox,
  createIcon,
  createTextarea,
  createDivider,
  createListView,
  createListViewItem,
  createPaper,
  createTab,
  createTabItem,
  createTabPanel,
  createBadge,
  createMenu,
  createMenuItem,
  openMenuAt,
  closeMenu,
  createSplit,
  createContextMenu,
  createLink,
  createToolbar
};
